import React, { useState, useEffect } from 'react';
import { useAuth } from '../../services/contexts/AuthContext';
import { 
  Users, 
  UserCheck, 
  Activity, 
  Heart, 
  TrendingUp, 
  Calendar,
  Clock,
  AlertTriangle,
  CheckCircle,
  XCircle,
  BarChart3,
  FileText,
  MessageSquare,
  Phone,
  Mail,
  Shield,
  Database,
  Zap,
  Stethoscope,
  Monitor,
  Target,
  Award,
  Star,
  Eye,
  EyeOff,
  Settings
} from 'lucide-react';
import toast from 'react-hot-toast';

const DashboardView = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalPatients: 0,
    activeRecordings: 0,
    completedRecordings: 0,
    pendingRecordings: 0,
    systemHealth: 'Good',
    lastUpdated: new Date()
  });

  useEffect(() => {
    // Simulate loading data
    setTimeout(() => {
      setStats({
        totalPatients: 156,
        activeRecordings: 24,
        completedRecordings: 89,
        pendingRecordings: 12,
        systemHealth: 'Good',
        lastUpdated: new Date()
      });
      setLoading(false);
    }, 1000);
  }, []);

  const getWelcomeMessage = () => {
    const hour = new Date().getHours();
    let greeting = '';
    
    if (hour < 12) greeting = 'Good Morning';
    else if (hour < 17) greeting = 'Good Afternoon';
    else greeting = 'Good Evening';
    
    return `${greeting}, ${user?.name || 'User'}!`;
  };

  const getRoleSpecificStats = () => {
    const baseStats = [
      {
        title: 'Total Patients',
        value: stats.totalPatients,
        icon: Users,
        color: 'brand-blue',
        change: '+12%',
        changeType: 'positive'
      },
      {
        title: 'Active Recordings',
        value: stats.activeRecordings,
        icon: Activity,
        color: 'brand-cyan',
        change: '+5%',
        changeType: 'positive'
      },
      {
        title: 'Completed',
        value: stats.completedRecordings,
        icon: CheckCircle,
        color: 'va-health-600',
        change: '+8%',
        changeType: 'positive'
      },
      {
        title: 'Pending',
        value: stats.pendingRecordings,
        icon: Clock,
        color: 'va-warning-600',
        change: '-3%',
        changeType: 'negative'
      }
    ];

    if (user?.role === 'patient') {
      return [
        {
          title: 'My Recordings',
          value: 8,
          icon: Activity,
          color: 'brand-cyan',
          change: '+2',
          changeType: 'positive'
        },
        {
          title: 'Health Score',
          value: '85%',
          icon: Heart,
          color: 'va-health-600',
          change: '+5%',
          changeType: 'positive'
        },
        {
          title: 'Last Recording',
          value: '2 days ago',
          icon: Calendar,
          color: 'va-primary-600',
          change: 'On track',
          changeType: 'neutral'
        },
        {
          title: 'Health Alerts',
          value: 0,
          icon: AlertTriangle,
          color: 'va-error-600',
          change: 'All clear',
          changeType: 'positive'
        }
      ];
    }

    return baseStats;
  };

  const getRecentActivity = () => {
    const activities = [
      {
        id: 1,
        type: 'recording',
        message: 'New recording completed for Patient #1234',
        time: '2 minutes ago',
        icon: Activity,
        color: 'brand-cyan'
      },
      {
        id: 2,
        type: 'patient',
        message: 'New patient registered: John Doe',
        time: '15 minutes ago',
        icon: UserCheck,
        color: 'brand-blue'
      },
      {
        id: 3,
        type: 'system',
        message: 'System health check completed',
        time: '1 hour ago',
        icon: Shield,
        color: 'va-health-600'
      },
      {
        id: 4,
        type: 'alert',
        message: 'Vital signs alert for Patient #5678',
        time: '2 hours ago',
        icon: AlertTriangle,
        color: 'va-warning-600'
      }
    ];

    return activities.slice(0, 4);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-blue"></div>
      </div>
    );
  }

  const statCards = getRoleSpecificStats();
  const recentActivity = getRecentActivity();

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-brand-blue to-brand-darkCyan rounded-lg p-6 text-white">
        <h2 className="text-2xl font-bold mb-2">{getWelcomeMessage()}</h2>
        <p className="text-va-primary-100">
          {user?.role === 'patient' 
            ? 'Monitor your health and track your vital signs.' 
            : 'Here\'s what\'s happening with your patients and system today.'
          }
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div key={index} className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className={`p-3 rounded-full bg-${stat.color}-100`}>
                  <Icon className={`h-6 w-6 text-${stat.color}-600`} />
                </div>
                <div className="ml-4">
                  <h3 className="text-2xl font-bold text-va-neutral-900">{stat.value}</h3>
                  <p className="text-va-neutral-600 text-sm">{stat.title}</p>
                </div>
              </div>
              <div className="mt-4 flex items-center">
                <span className={`text-xs font-medium ${
                  stat.changeType === 'positive' ? 'text-va-health-600' : 
                  stat.changeType === 'negative' ? 'text-va-error-600' : 
                  'text-va-neutral-600'
                }`}>
                  {stat.change}
                </span>
                <span className="text-xs text-va-neutral-500 ml-1">from last week</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Activity */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-va-neutral-900">Recent Activity</h3>
            <button className="text-sm text-brand-blue hover:text-brand-darkCyan">View All</button>
          </div>
          <div className="space-y-4">
            {recentActivity.map((activity) => {
              const Icon = activity.icon;
              return (
                <div key={activity.id} className="flex items-start space-x-3">
                  <div className={`p-2 rounded-full bg-${activity.color}-100`}>
                    <Icon className={`h-4 w-4 text-${activity.color}-600`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-va-neutral-900">{activity.message}</p>
                    <p className="text-xs text-va-neutral-500">{activity.time}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-va-neutral-900 mb-4">Quick Actions</h3>
          <div className="grid grid-cols-2 gap-4">
            <button className="p-4 border border-va-neutral-200 rounded-lg hover:border-brand-blue hover:bg-va-primary-50 transition-colors">
              <div className="flex items-center space-x-3">
                <UserCheck className="h-5 w-5 text-brand-blue" />
                <span className="text-sm font-medium text-va-neutral-900">Add Patient</span>
              </div>
            </button>
            <button className="p-4 border border-va-neutral-200 rounded-lg hover:border-brand-cyan hover:bg-va-accent-50 transition-colors">
              <div className="flex items-center space-x-3">
                <Activity className="h-5 w-5 text-brand-cyan" />
                <span className="text-sm font-medium text-va-neutral-900">Start Recording</span>
              </div>
            </button>
            <button className="p-4 border border-va-neutral-200 rounded-lg hover:border-va-health-600 hover:bg-va-health-50 transition-colors">
              <div className="flex items-center space-x-3">
                <BarChart3 className="h-5 w-5 text-va-health-600" />
                <span className="text-sm font-medium text-va-neutral-900">View Reports</span>
              </div>
            </button>
            <button className="p-4 border border-va-neutral-200 rounded-lg hover:border-va-primary-600 hover:bg-va-primary-50 transition-colors">
              <div className="flex items-center space-x-3">
                <Settings className="h-5 w-5 text-va-primary-600" />
                <span className="text-sm font-medium text-va-neutral-900">Settings</span>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* System Status */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-va-neutral-900">System Status</h3>
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-va-health-600 rounded-full"></div>
            <span className="text-sm text-va-neutral-600">All systems operational</span>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center space-x-3 p-3 bg-va-health-50 rounded-lg">
            <CheckCircle className="h-5 w-5 text-va-health-600" />
            <div>
              <p className="text-sm font-medium text-va-neutral-900">Database</p>
              <p className="text-xs text-va-neutral-500">Connected</p>
            </div>
          </div>
          <div className="flex items-center space-x-3 p-3 bg-va-health-50 rounded-lg">
            <CheckCircle className="h-5 w-5 text-va-health-600" />
            <div>
              <p className="text-sm font-medium text-va-neutral-900">API Services</p>
              <p className="text-xs text-va-neutral-500">Running</p>
            </div>
          </div>
          <div className="flex items-center space-x-3 p-3 bg-va-health-50 rounded-lg">
            <CheckCircle className="h-5 w-5 text-va-health-600" />
            <div>
              <p className="text-sm font-medium text-va-neutral-900">Storage</p>
              <p className="text-xs text-va-neutral-500">Available</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardView;